<template>
    <ds-modal
        :is-open="isOpen"
        show-close
        class="setup-modal"
        @close="$emit('close')"
    >
        <template #title>
            <span class="modal-title">
                Keap Marketing Number setup
            </span>
        </template>

        <div class="sms-setup-modal">
            <section class="intro">
                <ds-illustration class="illustration" name="phone-number-ready-color" />

                <h3 class="title">
                    Let's set up your Keap Marketing Number
                </h3>

                <p class="description">
                    It's your designated number for sending any automated or broadcast text messages
                </p>

                <span class="availability">
                    Only available in the U.S.
                </span>
            </section>

            <section class="terms">
                <p class="terms-description">
                    Please review and agree to these terms and policies.
                </p>

                <ds-checkbox
                    v-model="agreeToTerms"
                    class="terms-checkbox"
                >
                    <span>I agree to the <a
                        :href="termsAndConditionsLink"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop
                    >terms and conditions</a></span>
                </ds-checkbox>

                <ds-checkbox
                    v-model="agreeToPrivacy"
                    class="privacy-checkbox"
                >
                    <span>I agree to the <a
                        :href="privacyPolicyLink"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop
                    >privacy policy</a></span>
                </ds-checkbox>

                <div class="action-row">
                    <ds-button
                        class="action-button"
                        :loading="loading"
                        @click="submit"
                    >
                        Get my number
                    </ds-button>

                    <ds-status v-if="showError" class="error-status" type="error">
                        You must agree to the terms and conditions and privacy policy to continue
                    </ds-status>
                </div>
            </section>
        </div>
    </ds-modal>
</template>
<script>
import axios from 'axios';

import { trackSmsAccountProvisioningComplete } from '../smsAnalytics';
import { isSpaApp } from '@/api/shared';

export default {
    name: 'SmsSetupModal',

    props: {
        isOpen: Boolean,
        eventSource: {
            type: String,
            default: 'Automations',
        },
    },

    data() {
        return {
            agreeToPrivacy: false,
            agreeToTerms: false,
            agreeToPricing: false,
            loading: false,
            privacyPolicyLink: 'https://keap.com/legal/privacy-policy',
            showError: false,
            termsAndConditionsLink: '/resources/vue/files/sms-broadcast-tos.pdf',
            isSpaApp: false,
        };
    },

    mounted() {
        this.checkApp();
    },

    watch: {
        hasError(value) {
            if (!value) {
                this.showError = false;
            }
        },
    },

    computed: {
        hasError() {
            return !this.agreeToPrivacy || !this.agreeToTerms;
        },
    },

    methods: {
        async checkApp() {
            this.isSpaApp = await isSpaApp();
        },

        async submit() {
            if (this.hasError) {
                this.showError = true;
            } else {
                this.loading = true;

                try {
                    const { data: account } = await axios.post('/app/automatedSmsFunnel/provisionAccount');

                    trackSmsAccountProvisioningComplete(this.eventSource, this.isSpaApp);
                    this.$emit('next', account);
                } catch (error) {
                    this.$toast({ message: 'There was an error creating your account, please try again.' });
                }

                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .modal-title {
        font-size: $font-size-lg;
        font-weight: 600;
    }

    .intro {
        display: flex;
        flex-direction: column;
        padding: $spacing-400 $spacing-400 0;
    }

    .terms {
        display: flex;
        flex-direction: column;
        padding: $spacing-400;
        background-color: $color-orange-050;
        border-radius: $border-radius;
    }

    .illustration {
        align-self: center;
        margin-bottom: $spacing-400;
    }

    .title {
        line-height: $line-height-xl;
        text-align: center;
        font-size: $font-size-xl;
        font-weight: 500;
        padding: 0 $spacing-600;
    }

    .description {
        padding: $spacing-200 6rem $spacing-100;
        margin-bottom: 0;
        text-align: center;
        font-size: $font-size-md;
        line-height: $line-height-md;
    }

    .availability {
        margin-bottom: $spacing-400;
        text-align: center;
        font-size: $font-size-md;
        line-height: $line-height-md;
        color: $color-gray-700;
    }

    .sms-setup-modal {
        justify-content: center;
    }

    .terms-description {
        font-size: px-to-rem(16px);
        padding-bottom: $spacing-200;
    }

    .terms-checkbox,
    .privacy-checkbox,
    .pricing-checkbox {
        --checkbox-padding: #{0 0 $spacing-200};
    }

    .pricing-checkbox {
        display: inline-flex;
        align-items: flex-start;
        margin-bottom: $spacing-100;
    }

    .action-row {
        display: flex;
        align-items: flex-start;
        margin-bottom: $spacing-100;
    }

    .action-button {
        min-width: fit-content;
        @include margin-end($spacing-200);
        &.loading {
          display: block !important;
        }
    }

    .error-status {
        padding-top: $spacing-100;
        flex: 1;
    }

    .setup-modal {
        --modal-padding: 0;
    }
</style>
